import React, { useState, useEffect } from 'react';
import {
  X,
  Users,
  UserPlus,
  UserMinus,
  Search,
  Loader2,
  Crown,
  RefreshCw
} from 'lucide-react';
import { TeamDetail } from '../../types/settings/TeamTypes';
import { getTeamMembers, addTeamMembers, removeTeamMembers } from '@/actions/data-fetch-teams';
import { getUsers } from '@/actions/data-fetch-users';
import { toastMessage } from '@/utils/toast-message';

interface TeamMembersModalProps {
  team: TeamDetail;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

interface TeamMember {
  uuid: string;
  userName: string;
  userEmail: string;
  phone?: string;
  role?: {
    uuid: string;
    role_name: string;
    role_level?: number;
  };
}

const TeamMembersModal: React.FC<TeamMembersModalProps> = ({
  team,
  isOpen,
  onClose,
  onUpdate
}) => {
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [teamLead, setTeamLead] = useState<TeamMember | null>(null);
  const [availableUsers, setAvailableUsers] = useState<any[]>([]);
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen && team) {
      fetchTeamMembers();
    }
  }, [isOpen, team]);

  // Fetch available users whenever members change
  useEffect(() => {
    if (isOpen && team) {
      fetchAvailableUsers();
    }
  }, [isOpen, team, members]);

  const fetchTeamMembers = async () => {
    try {
      setIsLoading(true);
      const response = await getTeamMembers(team.uuid);

      if (response.success || response.status === 'success') {
        const data = response.data;
        setMembers(data.members || []);
        setTeamLead(data.team_lead);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      toastMessage('Failed to load team members', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAvailableUsers = async () => {
    try {
      const response = await getUsers({ limit: 100 });

      if (response.success || response.status === 'success') {
        const users = response.data?.users || [];
        console.log('Fetched users from API:', users.length, 'users');

        // Log detailed user info to identify the problematic user
        console.log('All users from API:', users.map((user: any) => ({
          uuid: user.uuid,
          name: user.userName,
          email: user.userEmail,
          active: user.user_active,
          deleted: user.deleted_at,
          team: user.team?.team_name || 'No team',
          isProblematic: user.uuid === '67a95699-7f58-40f1-b89e-7b061f8a06e7'
        })));

        // Log users that don't have proper data
        const invalidUsers = users.filter((user: any) => !user.uuid || !user.userName);
        if (invalidUsers.length > 0) {
          console.warn('Found users with invalid data:', invalidUsers);
        }

        // Filter out users already in the team and validate user data
        const memberUuids = members.map(m => m.uuid);
        const available = users.filter((user: any) =>
          user.uuid && // Ensure user has a valid UUID
          user.userName && // Ensure user has a name
          !memberUuids.includes(user.uuid) &&
          (!user.team || user.team?.uuid !== team.uuid)
        );

        console.log('Available users for team assignment:', available.length, 'users');
        setAvailableUsers(available);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toastMessage('Failed to load available users', 'error');
    }
  };

  const handleAddMembers = async () => {
    if (selectedUsers.length === 0) {
      toastMessage('Please select users to add', 'warning');
      return;
    }

    // Validate selected users before sending to API
    const invalidUsers = selectedUsers.filter(uuid => {
      const user = availableUsers.find(u => u.uuid === uuid);
      return !user || !user.uuid || !user.userName;
    });

    if (invalidUsers.length > 0) {
      console.error('Invalid user UUIDs found:', invalidUsers);
      toastMessage('Some selected users are invalid. Please refresh and try again.', 'error');
      await fetchAvailableUsers();
      setSelectedUsers([]);
      return;
    }

    // Log the users being added for debugging
    const usersToAdd = selectedUsers.map(uuid =>
      availableUsers.find(u => u.uuid === uuid)
    ).filter(Boolean);

    console.log('Adding users to team:', {
      teamUuid: team.uuid,
      userUuids: selectedUsers,
      userDetails: usersToAdd.map(u => ({
        uuid: u.uuid,
        name: u.userName,
        email: u.userEmail,
        phone: u.phone,
        role: u.role?.role_name,
        team: u.team?.team_name || 'No team'
      }))
    });

    // Check for potential backend constraints before API call
    console.log('=== BACKEND CONSTRAINT ANALYSIS ===');
    console.log('Team:', { uuid: team.uuid, name: team.team_name });
    console.log('Current members count:', members.length);
    console.log('Users to add:', selectedUsers.length);
    console.log('Total after addition would be:', members.length + selectedUsers.length);

    // Check for duplicate members (backend might reject even if frontend filters)
    const currentMemberUuids = members.map(m => m.uuid);
    const duplicates = selectedUsers.filter(uuid => currentMemberUuids.includes(uuid));
    if (duplicates.length > 0) {
      console.warn('⚠️  DUPLICATE DETECTED: Users already in team:', duplicates);
    }

    // Log each user being added with validation details
    selectedUsers.forEach((uuid, index) => {
      const user = availableUsers.find(u => u.uuid === uuid);
      console.log(`User ${index + 1}/${selectedUsers.length}:`, {
        uuid,
        exists_in_available: !!user,
        userName: user?.userName,
        userEmail: user?.userEmail,
        role: user?.role?.role_name,
        hasValidData: !!(user?.uuid && user?.userName && user?.userEmail),
        isAlreadyMember: currentMemberUuids.includes(uuid)
      });
    });

    // Note: Fixed issue where inactive users appeared in frontend but were rejected by backend
    // Backend validation requires user_active: true, frontend now filters to only show active users

    try {
      setIsLoading(true);
      console.log('📤 API Request:', {
        endpoint: `/teams/${team.uuid}/members`,
        method: 'POST',
        body: { user_uuids: selectedUsers }
      });

      const response = await addTeamMembers(team.uuid, selectedUsers);

      console.log('📥 API Response:', response);

      if (response.success || response.status === 'success') {
        console.log('✅ SUCCESS: Members added successfully');
        toastMessage(`Added ${selectedUsers.length} member(s) successfully`, 'success');
        setSelectedUsers([]);
        setIsAddMode(false);
        await fetchTeamMembers();
        await fetchAvailableUsers();
        onUpdate?.();
      } else {
        // Enhanced error analysis
        const errorMessage = response.message || 'Failed to add members';
        console.error('❌ BACKEND ERROR ANALYSIS:', {
          error: errorMessage,
          selectedUsers,
          teamUuid: team.uuid,
          fullResponse: response,
          possibleCauses: [
            'User does not exist in backend database',
            'User account is inactive/disabled',
            'Team size limit exceeded',
            'User role restrictions',
            'User already in another team (exclusive membership)',
            'Database constraint violation',
            'Permission/authorization issue'
          ]
        });

        // More specific error handling
        if (errorMessage.includes('not found')) {
          console.warn('🔍 USER NOT FOUND - Possible causes:');
          console.warn('- User was deleted from backend but still appears in frontend');
          console.warn('- User account is deactivated');
          console.warn('- Database inconsistency');

          toastMessage('User not found in system. This may indicate the user account was deactivated or removed. Please contact your administrator.', 'error');
          setSelectedUsers([]);
          await fetchAvailableUsers();
        } else if (errorMessage.includes('already') || errorMessage.includes('duplicate')) {
          console.warn('🔄 DUPLICATE MEMBER ISSUE');
          toastMessage('User is already a team member', 'error');
        } else if (errorMessage.includes('limit') || errorMessage.includes('maximum')) {
          console.warn('📊 TEAM SIZE LIMIT REACHED');
          toastMessage('Team size limit reached. Cannot add more members.', 'error');
        } else {
          console.warn('🔧 UNKNOWN BACKEND CONSTRAINT');
          toastMessage(`Backend error: ${errorMessage}`, 'error');
        }
      }
    } catch (error: any) {
      console.error('Error adding members:', error);
      toastMessage(error.message || 'Failed to add members', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveMembers = async () => {
    if (selectedMembers.length === 0) {
      toastMessage('Please select members to remove', 'warning');
      return;
    }

    if (!confirm(`Remove ${selectedMembers.length} member(s) from ${team.team_name}?`)) {
      return;
    }

    try {
      setIsLoading(true);
      const response = await removeTeamMembers(team.uuid, selectedMembers);

      if (response.success || response.status === 'success') {
        toastMessage(`Removed ${selectedMembers.length} member(s) successfully`, 'success');
        setSelectedMembers([]);
        await fetchTeamMembers();
        await fetchAvailableUsers();
        onUpdate?.();
      } else {
        toastMessage(response.message || 'Failed to remove members', 'error');
      }
    } catch (error: any) {
      console.error('Error removing members:', error);
      toastMessage(error.message || 'Failed to remove members', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredUsers = availableUsers.filter(user =>
    user.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.userEmail?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] overflow-hidden">
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />
      <div className="fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
        <div className="w-screen max-w-2xl">
          <div className="h-full flex flex-col bg-white dark:bg-gray-800 shadow-xl">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Users className="w-6 h-6 text-blue-600" />
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                    Manage Team Members
                  </h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {team.team_name}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => {
                    fetchTeamMembers();
                    fetchAvailableUsers();
                    setSelectedUsers([]);
                    setSelectedMembers([]);
                    toastMessage('User data refreshed', 'success');
                  }}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                  title="Refresh user data"
                >
                  <RefreshCw className="w-4 h-4 text-gray-400" />
                </button>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>
            </div>

            {/* Mode Toggle */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    setIsAddMode(false);
                    setSelectedUsers([]);
                    setSelectedMembers([]);
                  }}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    !isAddMode
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Current Members ({members.length})
                </button>
                <button
                  onClick={() => {
                    setIsAddMode(true);
                    setSelectedUsers([]);
                    setSelectedMembers([]);
                  }}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                    isAddMode
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  Add Members ({availableUsers.length} available)
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-hidden">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
                    <p className="text-gray-600 dark:text-gray-400">Loading...</p>
                  </div>
                </div>
              ) : isAddMode ? (
                /* Add Members Mode */
                <div className="h-full flex flex-col">
                  {/* Search */}
                  <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search users..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Available Users List */}
                  <div className="flex-1 overflow-y-auto p-4">
                    {filteredUsers.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">
                          {searchTerm ? 'No users found matching your search' : 'No available users to add'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredUsers.map((user) => (
                          <label
                            key={user.uuid}
                            className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedUsers.includes(user.uuid)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedUsers([...selectedUsers, user.uuid]);
                                } else {
                                  setSelectedUsers(selectedUsers.filter(id => id !== user.uuid));
                                }
                              }}
                              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-3"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {user.userName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {user.userEmail}
                              </p>
                              {user.role && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  {user.role.role_name}
                                </p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Add Members Action */}
                  {selectedUsers.length > 0 && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleAddMembers}
                        disabled={isLoading}
                        className="w-auto flex items-center justify-center px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <UserPlus className="w-4 h-4 mr-2" />
                        Add {selectedUsers.length} Member(s)
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                /* Current Members Mode */
                <div className="h-full flex flex-col">
                  <div className="flex-1 overflow-y-auto p-4">
                    {members.length === 0 ? (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500 dark:text-gray-400">No team members yet</p>
                        <button
                          onClick={() => setIsAddMode(true)}
                          className="mt-4 px-3 py-1.5 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                        >
                          Add First Member
                        </button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {/* Team Lead */}
                        {teamLead && (
                          <div className="p-3 rounded-lg border-2 border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20">
                            <div className="flex items-center">
                              <Crown className="w-5 h-5 text-yellow-500 mr-3" />
                              <div className="flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                  {teamLead.userName} <span className="text-yellow-600 dark:text-yellow-400">(Team Lead)</span>
                                </p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                  {teamLead.userEmail}
                                </p>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Regular Members */}
                        {members.filter(m => m.uuid !== teamLead?.uuid).map((member) => (
                          <label
                            key={member.uuid}
                            className="flex items-center p-3 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={selectedMembers.includes(member.uuid)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedMembers([...selectedMembers, member.uuid]);
                                } else {
                                  setSelectedMembers(selectedMembers.filter(id => id !== member.uuid));
                                }
                              }}
                              className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded mr-3"
                            />
                            <div className="flex-1">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">
                                {member.userName}
                              </p>
                              <p className="text-xs text-gray-500 dark:text-gray-400">
                                {member.userEmail}
                              </p>
                              {member.role && (
                                <p className="text-xs text-blue-600 dark:text-blue-400">
                                  {member.role.role_name}
                                </p>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Remove Members Action */}
                  {selectedMembers.length > 0 && (
                    <div className="p-4 border-t border-gray-200 dark:border-gray-700">
                      <button
                        onClick={handleRemoveMembers}
                        disabled={isLoading}
                        className="w-full flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      >
                        <UserMinus className="w-4 h-4 mr-2" />
                        Remove {selectedMembers.length} Member(s)
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamMembersModal;